import type { ComponentPropsWithoutRef, ElementRef } from 'react'
import { createContext, forwardRef, use } from 'react'
import { FOCUS_RING } from '@/lib/focus-ring'
import { cn } from '@/lib/utils'
import * as TabsPrimitive from '@radix-ui/react-tabs'

type TabsVariant = 'underline' | 'pill'

const TabsVariantContext = createContext<TabsVariant>('underline')

const Tabs = TabsPrimitive.Root

interface TabsListProps extends ComponentPropsWithoutRef<
  typeof TabsPrimitive.List
> {
  variant?: TabsVariant
}

const TabsList = forwardRef<
  ElementRef<typeof TabsPrimitive.List>,
  TabsListProps
>(({ className, variant = 'underline', ...props }, ref) => (
  <TabsVariantContext.Provider value={variant}>
    <TabsPrimitive.List
      ref={ref}
      className={cn(
        variant === 'underline' &&
          'inline-flex h-10 items-center gap-1 border-b border-border/40',
        variant === 'pill' &&
          'inline-flex items-center justify-center rounded-full bg-muted/50 p-1.5 text-muted-foreground shadow-sm',
        className,
      )}
      {...props}
    />
  </TabsVariantContext.Provider>
))
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = forwardRef<
  ElementRef<typeof TabsPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => {
  const variant = use(TabsVariantContext)

  if (variant === 'pill') {
    return (
      <TabsPrimitive.Trigger
        ref={ref}
        className={cn(
          'transition-tonal inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium duration-150',
          FOCUS_RING,
          'disabled:pointer-events-none disabled:opacity-50',
          'data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm',
          className,
        )}
        {...props}
      />
    )
  }

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        'transition-tonal group relative inline-flex items-center justify-center gap-2 whitespace-nowrap px-4 py-2.5 text-sm font-medium text-muted-foreground duration-200',
        'hover:text-foreground/80',
        'focus-visible:text-foreground focus-visible:outline-none',
        'disabled:pointer-events-none disabled:opacity-50',
        'data-[state=active]:text-foreground',
        className,
      )}
      {...props}
    >
      {props.children}
      <span
        className={cn(
          'absolute bottom-0 left-0 right-0 h-0.5 scale-x-0 bg-primary transition-transform duration-200 ease-out',
          'group-data-[state=active]:scale-x-100',
        )}
      />
    </TabsPrimitive.Trigger>
  )
})
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = forwardRef<
  ElementRef<typeof TabsPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-4',
      FOCUS_RING,
      'data-[state=inactive]:animate-out data-[state=inactive]:fade-out-0',
      'data-[state=active]:animate-in data-[state=active]:fade-in-0',
      className,
    )}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
